/**
 * IssuesProgressBand — Belohnungsschleife der Optimierungen-Seite (P4/B6b, Mock A/2):
 * Geschlossen (7 Tage) · Ø Score (folgt Kanal-Facette) · Offene Verstöße.
 * Kein Fake-Trend: Prozent nur bei Vorwochen-Basis, Sparkline ab 2 Punkten.
 */
import { useTranslation } from 'react-i18next'

import { useIssuesProgress } from '@/hooks/useIssuesInsights'
import type { ProgressPoint } from '@/lib/api/issues-insights'

import { ProgressStatTile } from './ProgressStatTile'

/** Trend aus erstem/letztem Serienpunkt; kein Badge ohne echte Basis (Serie < 2 Punkte oder Startwert 0). */
function deriveOpenTrend(series: ProgressPoint[]) {
  if (series.length < 2 || series[0].value <= 0) return null
  const first = series[0].value
  const last = series[series.length - 1].value
  const openPct = Math.round(((last - first) / first) * 100)
  const fell = last < first
  return { text: `${fell ? '▼' : '▲'} ${Math.abs(openPct)} %`, positive: fell }
}

export function IssuesProgressBand({ channelId, channelName }: { channelId: string | null; channelName: string | null }) {
  const { t } = useTranslation()
  const { data: p, isLoading } = useIssuesProgress(channelId)
  if (isLoading || !p) return null

  const pct = p.closed_prev_7d > 0
    ? Math.round(((p.closed_last_7d - p.closed_prev_7d) / p.closed_prev_7d) * 100)
    : null
  const scoreScope = channelName ?? t('productsIssues.progress.allChannels')
  const openTrend = deriveOpenTrend(p.open_issues_series)

  return (
    <section aria-label={t('productsIssues.progress.bandLabel')} className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="issues-progress-band">
      <ProgressStatTile
        label={t('productsIssues.progress.closed7d')}
        value={p.closed_last_7d.toLocaleString('de-DE')}
        trend={pct === null ? null : { text: `${pct >= 0 ? '▲' : '▼'} ${Math.abs(pct)} %`, positive: pct >= 0 }}
        trendTestId="progress-closed-trend"
      />
      <ProgressStatTile
        label={`${t('productsIssues.progress.avgScore')} · ${scoreScope}`}
        value={p.avg_score.toLocaleString('de-DE')}
        series={p.avg_score_series}
        sparklineTestId="sparkline-avg-score"
      />
      <ProgressStatTile
        label={t('productsIssues.progress.openIssues')}
        value={p.open_issues.toLocaleString('de-DE')}
        trend={openTrend}
        trendTestId="progress-open-trend"
        series={p.open_issues_series}
        sparklineTestId="sparkline-open-issues"
      />
    </section>
  )
}
